import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorParser } from '@app/classes/error-parser.class';
import { AnalyticEvents, SocketEventTypes } from '@app/enums';
import { CanDeactivateComponent } from '@app/interfaces';
import { Payroll } from '@app/models/payroll/payroll.model';
import { XeroSyncStatus } from '@app/modules/payroll/interfaces/xero-sync-status.interface';
import { XeroConnectionStatus } from '@app/modules/settings/enums/xero-connection-status.enum';
import { XeroIntegrationService } from '@app/modules/settings/services/xero-integration.service';
import { AnalyticService, NotifyService, SocketService } from '@app/services';
import { TranslateService } from '@ngx-translate/core';
import { getYear, parse } from 'date-fns';
import { Subscription } from 'rxjs';

@Component({
    templateUrl: './payroll-completed.template.html',
    styleUrls: ['./payroll-completed.style.scss'],
})
export class PayrollCompletedView implements CanDeactivateComponent, OnInit, OnDestroy {
    payroll: Payroll;
    xeroCompanyName?: string;
    xeroConnectionStatus?: XeroConnectionStatus;
    showXeroInProgressWarningModal = false;
    private completedSteps = 0;
    private totalSteps = 0;
    private subscriptions: Subscription[] = [];
    private xeroLoaded = false;
    private xeroErrors: unknown[] = [];

    constructor(
        private analytics: AnalyticService,
        private route: ActivatedRoute,
        private xeroIntegrationService: XeroIntegrationService,
        private notify: NotifyService,
        private router: Router,
        private socket: SocketService,
        private translateService: TranslateService
    ) {
        this.payroll = this.route.snapshot.data.models.get('payroll');
    }

    async ngOnInit(): Promise<void> {
        this.getXeroNameAndStatus();
        this.subscribeToPayrollPayEvents();
    }

    ngOnDestroy(): void {
        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
    }

    @HostListener('window:beforeunload', ['$event'])
    warn($event: BeforeUnloadEvent): void {
        if (this.shouldWarnOnNavigate) {
            $event.returnValue = this.translateService.instant('payroll.run.windowUnloadMessage');
        }
    }

    canDeactivate(): boolean {
        if (!this.shouldWarnOnNavigate) {
            return true;
        }

        this.showXeroInProgressWarningModal = true;

        return false;
    }

    goToPayrollRegister(): void {
        this.analytics.trackEvent(
            this.payroll.recurring
                ? AnalyticEvents.PayrollRecurringPayrollReviewStepCompletedPayrollClickViewPayrollRegister
                : AnalyticEvents.PayrollOffCycleReviewStepCompletedPayrollClickViewPayrollRegister
        );

        const pathSegments = ['/reporting', 'payroll', 'payroll-register'];

        this.router.navigate(pathSegments);
    }

    get payrollRegisterUrl(): string {
        return this.router.serializeUrl(
            this.router.createUrlTree(['/reporting/payroll/payroll-register'], {
                queryParams: {
                    payrollType: this.payroll?.recurring ? 'recurring' : 'off-cycle',
                    payPeriod: this.payroll?.recurring ? this.payroll?.periodNo : this.payroll?.chequeDate,
                    year: getYear(parse(this.payroll?.chequeDate)),
                },
                queryParamsHandling: 'merge',
            })
        );
    }

    get shouldShowXeroCard(): boolean {
        return this.xeroLoaded;
    }

    get isConnectedToXero(): boolean {
        return this.xeroConnectionStatus === 'connected';
    }

    get hasXeroError(): boolean {
        return this.xeroErrors.length > 0;
    }

    get progress(): number {
        if (this.totalSteps === 0) {
            return 0;
        }

        return Math.round((this.completedSteps / this.totalSteps) * 100);
    }

    get shouldWarnOnNavigate(): boolean {
        return this.shouldShowXeroCard && this.isConnectedToXero && !this.hasXeroError && this.progress < 100;
    }

    get payrollType(): string {
        const type = this.payroll.recurring ? 'payroll.records.recurring' : 'payroll.records.offCycle';
        return this.translateService.instant(type);
    }

    get showQhsfRate(): boolean {
        return parseFloat(this.payroll.qhsfAmount) > 0;
    }

    private async getXeroNameAndStatus(): Promise<void> {
        const integration = await this.xeroIntegrationService.getXeroIntegrationDetails();

        if (integration) {
            this.xeroCompanyName = integration.xeroCompanyName;
            this.xeroConnectionStatus = integration.xeroConnectionStatus;
            this.xeroLoaded = true;
        }
    }

    /**
     * Set up subscriptions to payroll xero sync events
     */
    private subscribeToPayrollPayEvents(): void {
        this.subscriptions.push(
            this.socket
                .getCompanyChannel(SocketEventTypes.afterPayrollPaidProgress)
                .subscribe((progress) => this.updateProgress(progress)),

            this.socket
                .getCompanyChannel(SocketEventTypes.afterPayrollPaidCompleted)
                .subscribe((progress) => this.updateProgress(progress)),

            this.socket
                .getCompanyChannel(SocketEventTypes.afterPayrollPaidError)
                .subscribe((error) => this.handleError({ errors: [error] }))
        );
    }

    private updateProgress(progress: XeroSyncStatus): void {
        this.completedSteps = progress.completed;
        this.totalSteps = progress.total;
    }

    private handleError(error: Parameters<typeof ErrorParser.parse>[0]): void {
        this.xeroErrors.push(error);
        this.notify.error(ErrorParser.parse(error));
    }
}
